/**
 * AI Sales Agent Phase 4 increment 2 — guided opportunity qualification (rules only, no AI calls).
 * Pure helpers: compute qualification gaps, pick one focused question, and build reviewed write payloads.
 * Firestore writes live in opportunityQualificationActions.js; validation is mirrored in firestore.rules.
 */
import { sanitizeString } from './sanitization';

export const QUALIFICATION_VERSION = 'rules-qualification-v1';
export const QUALIFICATION_STATUSES = ['confirmed', 'unknown'];
export const QUALIFICATION_ANSWER_MAX = 1000;

const toDate = value => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Ordered by priority: the first unconfirmed item becomes the focused question.
export const QUALIFICATION_ITEMS = [
  {
    key: 'need', label: 'Need',
    question: 'What is driving this purchase, and what happens if nothing changes?',
    evidence: () => null
  },
  {
    key: 'buyer', label: 'Decision maker',
    question: 'Who signs off on this order, and who else weighs in on the decision?',
    evidence: opp => opp.contactName
      ? `Contact on file: ${opp.contactName}${opp.contactTitle ? ` (${opp.contactTitle})` : ''}` : null
  },
  {
    key: 'timing', label: 'Timing',
    question: 'When do you need product in stores, and what date is driving that?',
    evidence: (opp, { formatDate }) => {
      const due = toDate(opp.dueDate);
      return due ? `Due date on file: ${formatDate(due)}` : null;
    }
  },
  {
    key: 'budget', label: 'Budget',
    question: 'What budget or order size are you planning for this program?',
    evidence: (opp, { valueLabel }) => opp.value ? `Estimated value on file: ${valueLabel(opp.value)}` : null
  },
  {
    key: 'fit', label: 'Product fit',
    question: 'Which categories, styles and size ranges are you looking for, and what has sold well for you?',
    evidence: () => null
  }
];

export const QUALIFICATION_KEYS = QUALIFICATION_ITEMS.map(item => item.key);

const defaultFormatters = {
  formatDate: date => date.toLocaleDateString('en-US'),
  valueLabel: value => String(value)
};

/**
 * @returns {{ items: Array, confirmedCount: number, total: number, next: Object|null, stageHint: boolean }}
 * Item status: 'confirmed' | 'unknown' (asked, answer not known yet) | 'open' (never recorded).
 */
export function evaluateQualification(opp = {}, formatters = {}) {
  const fmt = { ...defaultFormatters, ...formatters };
  const recorded = opp.qualification && typeof opp.qualification === 'object' ? opp.qualification : {};
  const items = QUALIFICATION_ITEMS.map(def => {
    const entry = recorded[def.key];
    const status = entry && QUALIFICATION_STATUSES.includes(entry.status) ? entry.status : 'open';
    return {
      key: def.key, label: def.label, question: def.question, status,
      answer: typeof entry?.answer === 'string' ? entry.answer : '',
      evidence: def.evidence(opp, fmt),
      updatedByName: entry?.updatedByName || null,
      updatedAt: toDate(entry?.updatedAt)
    };
  });
  const confirmedCount = items.filter(item => item.status === 'confirmed').length;
  // Prefer never-asked items over ones already asked and still unknown.
  const next = items.find(item => item.status === 'open') || items.find(item => item.status === 'unknown') || null;
  return {
    items, confirmedCount, total: items.length, next,
    stageHint: opp.stage === 'New' && confirmedCount === items.length
  };
}

/**
 * Validates and sanitizes one answer. The caller adds `updatedAt: serverTimestamp()`.
 * `authUid` is the signed-in Firebase user; `user` may be a View As target.
 */
export function buildQualificationEntry({ status, answer }, { authUid, user }) {
  if (!QUALIFICATION_STATUSES.includes(status)) throw new Error('Choose Confirmed or Not known yet.');
  if (!authUid) throw new Error('A signed-in user is required.');
  const cleanAnswer = sanitizeString(answer || '', QUALIFICATION_ANSWER_MAX).trim();
  if (status === 'confirmed' && !cleanAnswer) throw new Error('Enter the answer before confirming.');
  const name = (user?.firstName && user?.lastName)
    ? `${user.firstName} ${user.lastName}` : (user?.displayName || user?.email || 'Unknown');
  return {
    status,
    answer: cleanAnswer,
    updatedByUid: authUid,
    updatedByName: sanitizeString(name, 200),
    actedAsUid: user?.uid && user.uid !== authUid ? user.uid : null
  };
}

const findItem = key => {
  const item = QUALIFICATION_ITEMS.find(def => def.key === key);
  if (!item) throw new Error(`Unknown qualification item: ${key}`);
  return item;
};

/** Initial values for TaskModal when creating a follow-up for one qualification question. */
export function buildQualificationTaskPrefill(opp, key, user, now = new Date()) {
  const item = findItem(key);
  const due = new Date(now);
  due.setUTCHours(12, 0, 0, 0);
  due.setUTCDate(due.getUTCDate() + 3);
  const label = opp.opportunityNumberLabel || opp.id;
  return {
    title: sanitizeString(`Qualify ${label}: ${item.label}`, 200),
    description: `Ask ${opp.contactName || 'the buyer'}: ${item.question}`,
    dueDate: due,
    uid: user?.uid || '',
    status: 'open',
    priority: 'Medium',
    customerId: opp.customerId || '',
    opportunityId: opp.id,
    associations: {
      customerId: opp.customerId || null,
      customerName: opp.customerName || null,
      opportunityId: opp.id,
      opportunityNumberLabel: label
    }
  };
}

/** Draft for EmailComposer. The rep reviews and sends it manually; nothing is sent automatically. */
export function buildQualificationEmailDraft(opp, key, user) {
  const item = findItem(key);
  const firstName = (opp.contactName || '').trim().split(/\s+/)[0] || '';
  return {
    uid: user?.uid || '',
    to: opp.contactEmail || '',
    subject: `Quick question about ${opp.customerName ? `${opp.customerName}'s` : 'your'} upcoming order`,
    body: `Hi${firstName ? ` ${firstName}` : ''},\n\n${item.question}\n\nThanks,\n${user?.firstName || user?.displayName || ''}`,
    from: user?.email
  };
}
